import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar, type ExportData } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { DateRangeSelect } from '../modules/DateRangeSelect';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { fmtLocalDateTime } from '../../utils/datetime';

// ─────────────────────────────────────────────────────────────────────────────
// Types — mirror ExceptionLogRow / ExceptionLogDetail (Application layer).
// ─────────────────────────────────────────────────────────────────────────────

/** One fault in the triage grid.  No stack trace — that arrives with the
 *  detail fetch, because a stack can be megabytes and the grid never shows it. */
interface ExceptionRow {
  oid:             string;
  occurredAt:      string;
  application:     string | null;
  environment:     string | null;
  machineName:     string | null;
  exceptionType:   string;
  message:         string;
  source:          string | null;
  severity:        number;          // 0 Critical · 1 Error · 2 Warning · 3 Info
  httpMethod:      string | null;
  requestPath:     string | null;
  queryString:     string | null;
  statusCode:      number | null;
  correlationId:   string | null;
  userName:        string | null;
  ipAddress:       string | null;
  userAgent:       string | null;
  isResolved:      boolean;
  resolvedBy:      string | null;
  resolvedAt:      string | null;
  resolutionNotes: string | null;
  occurrenceCount: number;
  firstOccurredAt: string | null;
  lastOccurredAt:  string | null;
  hasStackTrace:   boolean;
}

/** The same fault WITH the heavy text columns. */
interface ExceptionDetail extends Omit<ExceptionRow, 'hasStackTrace'> {
  stackTrace:     string | null;
  innerException: string | null;
}

interface ExceptionStats {
  distinctFaults:   number;
  totalOccurrences: number;
  unresolved:       number;
  critical:         number;
  distinctTypes:    number;
}

type StatusFilter = 'open' | 'resolved' | 'all';
type DateRangeKey = '24h' | '7d' | '30d' | '90d' | 'all';

// ─────────────────────────────────────────────────────────────────────────────
// Presentation helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Severity → label + pill class.  Values match the TINYINT column, so the
 *  order here is the storage contract, not a display choice. */
const SEVERITY: Record<number, { label: string; cls: string; icon: string }> = {
  0: { label: 'Critical', cls: 'alert', icon: 'ti-alert-octagon' },
  1: { label: 'Error',    cls: 'alert', icon: 'ti-alert-circle' },
  2: { label: 'Warning',  cls: 'warn',  icon: 'ti-alert-triangle' },
  3: { label: 'Info',     cls: 'info',  icon: 'ti-info-circle' },
};

function severityMeta(v: number) {
  return SEVERITY[v] ?? { label: String(v), cls: 'info', icon: 'ti-bug' };
}

/** Strip the namespace off an exception type — `System.NullReferenceException`
 *  reads as `NullReferenceException` in a narrow column.  The full name is kept
 *  on the title attribute and in the detail pane. */
function shortType(fullName: string): string {
  if (!fullName) return '—';
  const i = fullName.lastIndexOf('.');
  return i >= 0 ? fullName.slice(i + 1) : fullName;
}

/** `GET /api/v1/samples?x=1` — method + path, query trimmed to keep the column
 *  readable (the full value shows in the detail pane). */
function routeLabel(r: ExceptionRow): string {
  if (!r.requestPath) return '—';
  return `${r.httpMethod ?? ''} ${r.requestPath}`.trim();
}

function cutoffSince(range: DateRangeKey): string | undefined {
  if (range === 'all') return undefined;
  const ms = { '24h': 86400e3, '7d': 7 * 86400e3, '30d': 30 * 86400e3, '90d': 90 * 86400e3 }[range];
  return new Date(Date.now() - ms).toISOString();
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Settings → Exception Log.  The team's view of application faults recorded in
 * `dbo.AI_ExceptionLog` by `ExceptionHandlingMiddleware`.
 *
 * <p>Service / admin only — gated client-side via SERVICE_ONLY in
 * navPermissions.ts AND server-side in `ExceptionLogController`, because stack
 * traces expose internal structure.</p>
 *
 * <p>This is NOT the audit trail (Data Center → Audit Trail Log).  That records
 * business-data changes for compliance; this records software defects.</p>
 */
export default function ExceptionLogPage() {
  const [rows,      setRows]      = useState<ExceptionRow[]>([]);
  const [stats,     setStats]     = useState<ExceptionStats | null>(null);
  const [loading,   setLoading]   = useState(false);
  const [loadMs,    setLoadMs]    = useState<number | null>(null);
  const [error,     setError]     = useState<string>();
  const [status,    setStatus]    = useState<StatusFilter>('open');
  const [dateRange, setDateRange] = useState<DateRangeKey>('7d');
  const [search,    setSearch]    = useState('');
  const [detail,    setDetail]    = useState<ExceptionDetail | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [visibleRows, setVisibleRows] = useState<ExceptionRow[]>([]);
  const abortRef = useRef<AbortController>();

  const notify  = useNotify();
  const confirm = useConfirm();

  // ── Load ────────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true); setError(undefined);
    const t0 = performance.now();

    try {
      const params: Record<string, string> = { top: '1000' };
      const since = cutoffSince(dateRange);
      if (since) params.sinceUtc = since;
      if (status === 'open')     params.isResolved = 'false';
      if (status === 'resolved') params.isResolved = 'true';
      const qs = new URLSearchParams(params).toString();

      // List + KPI counters in parallel — the stats SP is an aggregate over the
      // same window, so serialising them would double the page's load time.
      const [listRes, statsRes] = await Promise.all([
        api.get<{ success: boolean; data: ExceptionRow[] }>(
          `/api/v1/exception-log?${qs}`, { signal: ctrl.signal }),
        api.get<{ success: boolean; data: ExceptionStats }>(
          `/api/v1/exception-log/stats${since ? `?sinceUtc=${encodeURIComponent(since)}` : ''}`,
          { signal: ctrl.signal }),
      ]);

      if (!ctrl.signal.aborted) {
        if (listRes.data.success) setRows(listRes.data.data);
        else setError('Failed to load the exception log.');
        if (statsRes.data.success) setStats(statsRes.data.data);
      }
    } catch (e: unknown) {
      if (!ctrl.signal.aborted) {
        const msg = e instanceof Error ? e.message : 'Unknown error';
        if (msg !== 'canceled' && msg !== 'CanceledError') setError(msg);
      }
    } finally {
      if (!ctrl.signal.aborted) {
        setLoadMs(performance.now() - t0);
        setLoading(false);
      }
    }
  }, [dateRange, status]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Detail (full stack trace) ───────────────────────────────────────────
  const openDetail = useCallback(async (row: ExceptionRow) => {
    setDetailBusy(true);
    try {
      const res = await api.get<{ success: boolean; data: ExceptionDetail }>(
        `/api/v1/exception-log/${row.oid}`);
      if (res.data.success) setDetail(res.data.data);
    } catch {
      notify.error('Could not load the exception detail.');
    } finally {
      setDetailBusy(false);
    }
  }, [notify]);

  // ── Resolve / re-open ───────────────────────────────────────────────────
  const toggleResolved = useCallback(async (row: ExceptionRow | ExceptionDetail) => {
    const closing = !row.isResolved;

    // Every state-changing action goes through the shared centred dialog
    // (standing rule 7) — never a native confirm().
    const ok = await confirm.confirm({
      title:   closing ? 'Mark as fixed?' : 'Re-open this fault?',
      message: closing
        ? `Mark "${shortType(row.exceptionType)}" as fixed? It will drop out of the open list, and a future occurrence will be logged as a new fault so a regression stays visible.`
        : `Re-open "${shortType(row.exceptionType)}"? It returns to the open list.`,
      tone: closing ? 'default' : 'danger',
    });
    if (!ok) return;

    try {
      await api.put(`/api/v1/exception-log/${row.oid}/resolve`, {
        isResolved: closing,
        notes: null,
      });
      notify.success(closing ? 'Marked as fixed.' : 'Re-opened.');
      setDetail(null);
      await loadData();
    } catch {
      notify.error('Could not update the fault.');
    }
  }, [confirm, notify, loadData]);

  // ── Client-side search over the loaded window ───────────────────────────
  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    if (!s) return rows;
    return rows.filter(r =>
      r.exceptionType.toLowerCase().includes(s) ||
      r.message.toLowerCase().includes(s) ||
      (r.source      ?? '').toLowerCase().includes(s) ||
      (r.requestPath ?? '').toLowerCase().includes(s) ||
      (r.userName    ?? '').toLowerCase().includes(s));
  }, [rows, search]);

  // ── Columns ─────────────────────────────────────────────────────────────
  const columns = useMemo<AppColumn<ExceptionRow>[]>(() => [
    {
      field: 'occurredAt', header: 'Occurred', type: 'datetime', width: 150,
      text: r => fmtLocalDateTime(r.occurredAt),
    },
    {
      field: 'severity', header: 'Severity', width: 105,
      text: r => severityMeta(r.severity).label,
      render: r => {
        const m = severityMeta(r.severity);
        return (
          <span className={`bl-pill ${m.cls}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
            <i className={`ti ${m.icon}`} style={{ fontSize: 12 }} /> {m.label}
          </span>
        );
      },
    },
    {
      field: 'exceptionType', header: 'Exception', width: 210,
      text: r => shortType(r.exceptionType),
      render: r => (
        <span title={r.exceptionType}
          style={{ fontWeight: 600, color: 'var(--ai-ink)', whiteSpace: 'nowrap' }}>
          {shortType(r.exceptionType)}
        </span>
      ),
    },
    {
      field: 'message', header: 'Message', minWidth: 260,
      render: r => (
        <span title={r.message} style={{ color: 'var(--ai-ink)' }}>{r.message}</span>
      ),
    },
    {
      field: 'requestPath', header: 'Route', width: 230,
      text: routeLabel,
      render: r => r.requestPath
        ? <code style={{ fontSize: 11, color: 'var(--ai-ink)', fontFamily: 'var(--ai-font-mono)' }}
            title={`${routeLabel(r)}${r.queryString ?? ''}`}>{routeLabel(r)}</code>
        : <span style={{ color: 'var(--ai-muted)' }}>—</span>,
    },
    {
      // The single most useful column on the page: one bug hit 4 000 times is
      // one row, and this is what says so.
      field: 'occurrenceCount', header: 'Hits', type: 'number', width: 80, align: 'right',
      render: r => (
        <span style={{ fontWeight: r.occurrenceCount > 1 ? 700 : 400, color: 'var(--ai-ink)' }}
          title={r.occurrenceCount > 1 && r.firstOccurredAt
            ? `First seen ${fmtLocalDateTime(r.firstOccurredAt)}`
            : undefined}>
          {r.occurrenceCount.toLocaleString()}
        </span>
      ),
    },
    {
      field: 'userName', header: 'User', width: 140,
      text: r => r.userName ?? '',
      render: r => r.userName
        ? <span style={{ color: 'var(--ai-ink)', whiteSpace: 'nowrap' }}>{r.userName}</span>
        : <span style={{ color: 'var(--ai-muted)' }}>—</span>,
    },
    {
      field: 'machineName', header: 'Server', width: 130,
      text: r => r.machineName ?? '',
    },
    {
      field: 'isResolved', header: 'Status', width: 110,
      text: r => (r.isResolved ? 'Fixed' : 'Open'),
      render: r => (
        <span className={`bl-pill ${r.isResolved ? 'ok' : 'warn'}`}
          title={r.isResolved && r.resolvedBy ? `Fixed by ${r.resolvedBy}` : undefined}
          style={{ whiteSpace: 'nowrap' }}>
          {r.isResolved ? 'Fixed' : 'Open'}
        </span>
      ),
    },
  ], []);

  // Export exactly what the grid shows — including any active column filter.
  const exportData: ExportData = useMemo(() => {
    const src = visibleRows.length > 0 ? visibleRows : filtered;
    const m = gridExportMatrix(src, columns);
    return { filename: 'exception-log', headers: m.headers, rows: m.rows };
  }, [visibleRows, filtered, columns]);

  const openCount = rows.filter(r => !r.isResolved).length;

  return (
    <div className="bl-page">

      {/* ── Fixed top ── */}
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Settings</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Exception Log</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="exceptionlog" loadMs={loadMs} loading={loading} />
          </div>
          <div className="dp-subtitle">
            <i className="ti ti-bug" style={{ fontSize: 13, verticalAlign: 'middle', marginRight: 4 }} />
            Application faults captured automatically · repeats are folded into one row ·
            {loading ? ' …' : ` ${openCount} open`}
          </div>
        </div>

        {error && (
          <div className="bl-err">
            <i className="ti ti-alert-circle" style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 5 }} />
            {error}
          </div>
        )}

        {/* KPI row */}
        <div className="kpi-row">
          <div className="kpi">
            <div className="kpi-lbl">Distinct Faults</div>
            <div className="kpi-val kpi-blue">{loading ? '…' : (stats?.distinctFaults ?? 0).toLocaleString()}</div>
            <div className="kpi-chg">{loading ? '…' : `${filtered.length.toLocaleString()} shown`}</div>
          </div>
          <div className="kpi">
            <div className="kpi-lbl">Total Occurrences</div>
            <div className="kpi-val kpi-teal">{loading ? '…' : (stats?.totalOccurrences ?? 0).toLocaleString()}</div>
            <div className="kpi-chg">incl. repeats</div>
          </div>
          <div className="kpi">
            <div className="kpi-lbl">Open</div>
            <div className="kpi-val" style={{ color: 'var(--ai-amber, #b45309)' }}>
              {loading ? '…' : (stats?.unresolved ?? 0).toLocaleString()}
            </div>
            <div className="kpi-chg">awaiting a fix</div>
          </div>
          <div className="kpi">
            <div className="kpi-lbl">Critical</div>
            <div className="kpi-val" style={{ color: 'var(--ai-red, #b91c1c)' }}>
              {loading ? '…' : (stats?.critical ?? 0).toLocaleString()}
            </div>
            <div className="kpi-chg">highest severity</div>
          </div>
        </div>
      </div>

      {/* ── Grid area ── */}
      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <DateRangeSelect variant="dropdown" label="Date Range"
                ranges={[
                  { label: '24h', days: 1 }, { label: '7d', days: 7 },
                  { label: '30d', days: 30 }, { label: '90d', days: 90 },
                  { label: 'All', days: null },
                ]}
                value={({ '24h': 1, '7d': 7, '30d': 30, '90d': 90, all: null } as Record<DateRangeKey, number | null>)[dateRange]}
                onChange={(d) => setDateRange(d === 1 ? '24h' : d === 7 ? '7d' : d === 30 ? '30d' : d === 90 ? '90d' : 'all')} />
            }
            onRefresh={loadData}
            refreshing={loading}
            exportData={exportData}
          />
        </div>

        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input
              type="text"
              placeholder="Search exception, message, route or user…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          {filtered.length !== rows.length && (
            <span className="search-match-count">
              {filtered.length} match{filtered.length !== 1 ? 'es' : ''}
            </span>
          )}
        </div>

        <div className="bl-card bl-card-flex">
          {/* Status tabs — "Open" first because triage starts with what's broken. */}
          <div className="sa-filter-row">
            <span className="sa-filter-lbl">Status:</span>
            {([
              { id: 'open'     as StatusFilter, label: 'Open' },
              { id: 'resolved' as StatusFilter, label: 'Fixed' },
              { id: 'all'      as StatusFilter, label: 'All' },
            ]).map(t => (
              <button
                key={t.id}
                className={`sa-filter-btn${status === t.id ? ' active' : ''}`}
                onClick={() => setStatus(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <AppDataGrid<ExceptionRow>
            rows={filtered}
            columns={columns}
            getRowId={r => r.oid}
            loading={loading}
            defaultPageSize={20}
            initialSort={{ key: 'occurredAt', dir: 'desc' }}
            onRowClick={openDetail}
            onVisibleRowsChange={setVisibleRows}
            emptyText={
              status === 'open'
                ? 'No open faults in this window — nothing to fix right now.'
                : 'No faults match the current filters.'
            }
          />
        </div>
      </div>

      {/* ── Detail pane: the stack trace the developer actually came for ── */}
      {detail && (
        <div
          onClick={() => setDetail(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000,
                   display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, width: 'min(980px, 96vw)', maxHeight: '90vh',
                     display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.3)', overflow: 'hidden' }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                          padding: '14px 18px', borderBottom: '1px solid var(--ai-line)', background: 'var(--ai-header-bg)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
                <i className={`ti ${severityMeta(detail.severity).icon}`}
                   style={{ fontSize: 20, color: 'var(--ai-muted)', marginTop: 2 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: 'var(--ai-ink)', wordBreak: 'break-word' }}>
                    {detail.exceptionType}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ai-muted)', marginTop: 2 }}>
                    {fmtLocalDateTime(detail.occurredAt)}
                    {detail.occurrenceCount > 1 && <> · <strong>{detail.occurrenceCount.toLocaleString()} occurrences</strong></>}
                    {detail.machineName && <> · {detail.machineName}</>}
                    {detail.environment && <> · {detail.environment}</>}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button className="ca-btn" onClick={() => toggleResolved(detail)}>
                  <i className={`ti ${detail.isResolved ? 'ti-rotate' : 'ti-check'}`} />
                  {detail.isResolved ? 'Re-open' : 'Mark fixed'}
                </button>
                <button className="ca-btn" onClick={() => setDetail(null)}><i className="ti ti-x" /></button>
              </div>
            </div>

            {/* Context strip */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, padding: '10px 18px', fontSize: 12,
                          color: 'var(--ai-muted)', borderBottom: '1px solid var(--ai-line-soft)' }}>
              {detail.requestPath && (
                <span><i className="ti ti-route" style={{ marginRight: 4 }} />
                  {detail.httpMethod} {detail.requestPath}{detail.queryString}</span>
              )}
              {detail.userName && <span><i className="ti ti-user" style={{ marginRight: 4 }} />{detail.userName}</span>}
              {detail.ipAddress && <span><i className="ti ti-network" style={{ marginRight: 4 }} />{detail.ipAddress}</span>}
              {detail.correlationId && (
                <span title="Use this to find the same request in the server logs">
                  <i className="ti ti-link" style={{ marginRight: 4 }} />{detail.correlationId}
                </span>
              )}
              {detail.isResolved && detail.resolvedBy && (
                <span><i className="ti ti-check" style={{ marginRight: 4 }} />
                  Fixed by {detail.resolvedBy}{detail.resolvedAt ? ` · ${fmtLocalDateTime(detail.resolvedAt)}` : ''}</span>
              )}
            </div>

            {/* Body */}
            <div style={{ overflowY: 'auto', padding: '14px 18px 18px' }}>
              <div style={{ fontSize: 13, color: 'var(--ai-ink)', fontWeight: 600, marginBottom: 6 }}>
                Message
              </div>
              <div style={{ fontSize: 13, color: 'var(--ai-ink)', marginBottom: 16, wordBreak: 'break-word' }}>
                {detail.message}
              </div>

              {detail.source && (
                <>
                  <div style={{ fontSize: 13, color: 'var(--ai-ink)', fontWeight: 600, marginBottom: 6 }}>Source</div>
                  <div style={{ fontSize: 12, color: 'var(--ai-ink)', marginBottom: 16,
                                fontFamily: 'var(--ai-font-mono)', wordBreak: 'break-all' }}>
                    {detail.source}
                  </div>
                </>
              )}

              {detail.stackTrace && (
                <>
                  <div style={{ fontSize: 13, color: 'var(--ai-ink)', fontWeight: 600, marginBottom: 6 }}>
                    Stack trace
                  </div>
                  <pre style={{ fontSize: 11.5, lineHeight: 1.55, background: '#f8fafc',
                                border: '1px solid var(--ai-line-soft)', borderRadius: 8, padding: 12,
                                overflowX: 'auto', color: 'var(--ai-ink)', margin: '0 0 16px',
                                fontFamily: 'var(--ai-font-mono)' }}>
                    {detail.stackTrace}
                  </pre>
                </>
              )}

              {detail.innerException && (
                <>
                  <div style={{ fontSize: 13, color: 'var(--ai-ink)', fontWeight: 600, marginBottom: 6 }}>
                    Inner exception
                  </div>
                  <pre style={{ fontSize: 11.5, lineHeight: 1.55, background: '#fff7ed',
                                border: '1px solid var(--ai-line-soft)', borderRadius: 8, padding: 12,
                                overflowX: 'auto', color: 'var(--ai-ink)', margin: 0,
                                fontFamily: 'var(--ai-font-mono)' }}>
                    {detail.innerException}
                  </pre>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {detailBusy && !detail && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 999 }} aria-hidden />
      )}
    </div>
  );
}
